import { Store, Delivery } from '@one-thd/sui-icons';
import {
  FULFILLMENT_UI_LABELS,
  BROWSE_ALL_REFINEMENT,
  FULFILLMENT_DIMENSIONS_LABELS,
  REFINEMENT_LABELS,
  DELIVERY_REFINEMENTS_LABELS,
  GET_IT_FAST_DIMENSION_LABEL,
  PLP_SPEED_DIMENSION_LABEL,
  IN_STORE_CHIP_DIMENSION_LABEL,
  VALID_IN_STORE_DIMENSION_LABELS,
  FULFILLMENT_DIMENSION_LABELS_TO_CHECK
} from './constants';

export const filterDimensionsByLabel = (dimensions, labelToMatch) => {
  return dimensions.filter((dimension) => (dimension.label || '').toUpperCase() === labelToMatch.toUpperCase()
  );
};

export const getGetItFastRefinement = (dimensions, label) => {
  if (!dimensions || !Array.isArray(dimensions)) {
    return null;
  }

  const getItFastDimension = dimensions.find((dim) => dim?.label === GET_IT_FAST_DIMENSION_LABEL);
  if (!getItFastDimension?.refinements) {
    return null;
  }

  return getItFastDimension.refinements.find((refinement) => refinement?.label?.includes(label)) || null;
};

const createInStoreRefinement = (inStoreRefinement) => {
  return inStoreRefinement ? [{
    ...inStoreRefinement,
    label: FULFILLMENT_UI_LABELS.IN_STORE
  }] : [];
};

const findAnytimeRefinement = (dimension) => {
  return dimension?.refinements?.find((item) => item?.label === REFINEMENT_LABELS.ANYTIME);
};

const createDeliveryRefinement = (plpDelivery, label) => {
  if (!plpDelivery[0]) {
    return [];
  }

  const anytimeRefinement = findAnytimeRefinement(plpDelivery[0]);

  return [{
    label,
    refinements: plpDelivery[0]?.refinements || [],
    url: anytimeRefinement?.url || '',
    refinementKey: anytimeRefinement?.refinementKey || ''
  }];
};

export function createFulfillmentDimension(dimensions) {
  // Handle undefined or invalid dimensions parameter
  if (!dimensions || !Array.isArray(dimensions)) {
    return [{
      label: PLP_SPEED_DIMENSION_LABEL,
      dimensionId: 'customInStoreDelivery',
      refinements: [
        BROWSE_ALL_REFINEMENT
      ]
    }];
  }

  // Extract Get It Fast dimension and find "Pick Up Today" refinement
  const inStoreRefinement = getGetItFastRefinement(dimensions, REFINEMENT_LABELS.PICKUP_TODAY);

  // Extract Delivery and Free Delivery dimensions
  const plpDelivery = filterDimensionsByLabel(dimensions, FULFILLMENT_DIMENSIONS_LABELS.DELIVERY);
  const plpFreeDelivery = filterDimensionsByLabel(dimensions, FULFILLMENT_DIMENSIONS_LABELS.FREE_DELIVERY);

  // For PLP speed enabled, create custom plp-speed dimension
  return [{
    label: PLP_SPEED_DIMENSION_LABEL,
    dimensionId: 'customInStoreDelivery',
    refinements: [
      BROWSE_ALL_REFINEMENT,
      ...createInStoreRefinement(inStoreRefinement),
      ...createDeliveryRefinement(plpDelivery, FULFILLMENT_UI_LABELS.DELIVERY),
      ...createDeliveryRefinement(plpFreeDelivery, FULFILLMENT_UI_LABELS.FREE_DELIVERY)
    ]
  }];
}

/**
 * Returns the refinement that matches the given refinement key within the provided dimension.
 * @param {obj} dimension - The dimension object containing refinements.
 * @param {string} targetRefinementKey - The refinement key to match.
 * @returns {obj|null} - The matching refinement object or null if not found.
 */
export const findMatchingRefinementInDimension = (
  dimension,
  targetRefinementKey = BROWSE_ALL_REFINEMENT.refinementKey
) => {
  const directMatch = dimension.refinements?.find(
    (item) => item?.refinementKey === targetRefinementKey && !item?.refinements
  );

  if (directMatch) {
    return { refinement: directMatch, dimension };
  }

  const nestedMatch = dimension.refinements?.find((item) => {
    if (item.refinements && Array.isArray(item.refinements)) {
      return item.refinements.find(
        (nestedItem) => nestedItem?.refinementKey === targetRefinementKey
      );
    }
    return false;
  });

  if (nestedMatch) {
    const nestedRefinement = nestedMatch.refinements.find(
      (nestedItem) => nestedItem.refinementKey === targetRefinementKey
    );
    return { refinement: nestedRefinement, dimension: nestedMatch };
  }

  return null;
};

/**
 * Returns the Fulfillment dimension tile label to display based on the given dimension label.
 * @param {string} dimensionLabel - Label of the dimension
 * @returns {string} - Fulfillment label
 */
export const getFulfillmentTileLabel = (dimensionLabel) => {
  let updatedLabel;

  if (VALID_IN_STORE_DIMENSION_LABELS.includes(dimensionLabel)) {
    updatedLabel = FULFILLMENT_UI_LABELS.IN_STORE;
  } else if (/delivery/i.test(dimensionLabel)) {
    updatedLabel = FULFILLMENT_UI_LABELS.DELIVERY;
  } else {
    updatedLabel = FULFILLMENT_UI_LABELS.BROWSE_ALL;
  }

  return updatedLabel;
};

/**
 * Returns the subheader text for the fulfillment dimension.
 * @param {string} dimensionOrRefinementLabel - Label of the dimension or refinement
 * @param {string} storeName - Name of the store
 * @param {boolean} isConsumerApp - Flag indicating if user is on the app
 * @param {string} deliveryZip - Delivery zip code
 * @returns {string} - Fulfillment label
 */
export const getFulfillmentSubheaderLabel = (dimensionOrRefinementLabel, storeName, isConsumerApp, deliveryZip) => {
  let subHeaderLabel = dimensionOrRefinementLabel;

  if ((dimensionOrRefinementLabel === FULFILLMENT_UI_LABELS.IN_STORE
    || dimensionOrRefinementLabel === IN_STORE_CHIP_DIMENSION_LABEL)
    && storeName) {
    subHeaderLabel = `${FULFILLMENT_UI_LABELS.IN_STORE} at ${storeName}`;
  }
  if (dimensionOrRefinementLabel?.includes('Delivery') && !isConsumerApp) {
    subHeaderLabel = `${FULFILLMENT_UI_LABELS.DELIVERY} to ${deliveryZip || '...'}`;
  }

  return subHeaderLabel;
};

const isGetItFastLabelWithPlpSpeed = (dimensionLabel, isPlpSpeedEnabled) => {
  return isPlpSpeedEnabled && dimensionLabel === GET_IT_FAST_DIMENSION_LABEL;
};

/**
 * Returns true if the given dimension label is valid as a fulfillment dimension label.
 * @param {string} dimensionLabel - Label of the dimension
 * @param {boolean} isPlpSpeedEnabled - Flag indicating if the PLP Speed feature switch is enabled
 * @returns {boolean} - True if valid fulfillment dimension label, false otherwise
 */
export const isPlpDimensionLabel = (dimensionLabel, isPlpSpeedEnabled) => {
  const getItFastLabelWithPlpSpeed = isGetItFastLabelWithPlpSpeed(dimensionLabel, isPlpSpeedEnabled);

  return [
    ...FULFILLMENT_DIMENSION_LABELS_TO_CHECK,
    getItFastLabelWithPlpSpeed ? GET_IT_FAST_DIMENSION_LABEL : null
  ].filter(Boolean).includes(dimensionLabel);
};

/**
 * Resets the dimension label based on the refinement label and PLP speed feature flag.
 * @param {string} dimensionLabel - Label of the dimension
 * @param {string} refinementLabel - Label of the refinement
 * @param {boolean} isPlpSpeedEnabled - Flag indicating if the PLP Speed feature switch is enabled
 * @returns {string} - Updated dimension label
 */
export const resetDimensionLabel = (dimensionLabel, refinementLabel, isPlpSpeedEnabled) => {
  if (!isPlpSpeedEnabled) {
    return dimensionLabel;
  }

  const isFreeDelivery = dimensionLabel === FULFILLMENT_UI_LABELS.FREE_DELIVERY;
  let updatedDimensionLabel;

  if (refinementLabel === FULFILLMENT_UI_LABELS.IN_STORE
    || refinementLabel === REFINEMENT_LABELS.PICKUP_TODAY) {
    updatedDimensionLabel = IN_STORE_CHIP_DIMENSION_LABEL;
  } else if (/delivery/i.test(refinementLabel) || DELIVERY_REFINEMENTS_LABELS.includes(refinementLabel)) {
    updatedDimensionLabel = isFreeDelivery ? FULFILLMENT_UI_LABELS.FREE_DELIVERY : FULFILLMENT_UI_LABELS.DELIVERY;
  } else if (refinementLabel === FULFILLMENT_UI_LABELS.BROWSE_ALL) {
    updatedDimensionLabel = FULFILLMENT_UI_LABELS.BROWSE_ALL;
  } else {
    // Fallback for other non-fulfillment labels
    updatedDimensionLabel = dimensionLabel;
  }

  return updatedDimensionLabel;
};

const resetRefinementLabel = (refinementLabel, isPlpSpeedEnabled) => {
  let updatedRefinementLabel;

  // Pending: Consider special cases when the labels are customized for specific seasons/promotions, like the holidays
  if (isPlpSpeedEnabled
    && (refinementLabel === REFINEMENT_LABELS.PICKUP_TODAY || refinementLabel === 'In Stock at Store Today')) {
    updatedRefinementLabel = FULFILLMENT_UI_LABELS.IN_STORE;
  } else if (!isPlpSpeedEnabled && refinementLabel === REFINEMENT_LABELS.PICKUP_TODAY) {
    updatedRefinementLabel = 'In Stock at Store Today';
  } else {
    updatedRefinementLabel = refinementLabel;
  }

  return updatedRefinementLabel;
};

/**
 * Resets the chip title and text based on the dimension label, refinement label, and PLP speed feature flag.
 * @param {string} dimensionLabel - Label of the dimension
 * @param {string} refinementLabel - Label of the refinement
 * @param {boolean} isPlpSpeedEnabled - Flag indicating if the PLP Speed feature switch is enabled
 * @returns {[string, string]} A tuple where:
 *   - The first element (index 0) is the updated dimension label.
 *   - The second element (index 1) is the updated refinement label.
 */
export const getChipText = (dimensionLabel, refinementLabel, isPlpSpeedEnabled) => {
  const updatedDimensionLabel = resetDimensionLabel(
    dimensionLabel, refinementLabel, isPlpSpeedEnabled
  );
  const updatedRefinementLabel = resetRefinementLabel(refinementLabel, isPlpSpeedEnabled);

  return [
    updatedDimensionLabel,
    updatedRefinementLabel
  ];
};

export const getRefinementCount = (dim, refinementLabel) => {
  const refinement = dim?.refinements?.find(
    (refItem) => refItem?.label?.toLowerCase() === refinementLabel?.toLowerCase());
  if (!refinement) {
    return 0;
  }
  const count = refinement?.recordCount;
  return count && !Number.isNaN(parseInt(count, 10)) ? parseInt(count, 10) : 0;
};

/**
 * Finds a dimension in the provided array that matches one of the given labels.
 *
 * @param {Array} dimensions - Array of dimension objects.
 * @param {Array<string>} labels - Array of labels to search for.
 * @returns {Object|undefined} The found dimension object or undefined.
 */
export const findDimensionByLabels = (dimensions, labels) => {
  return dimensions?.find((dimension) => labels.some(
    (label) => dimension?.label?.toLowerCase() === label?.toLowerCase()
  ));
};

export const getAppliedDeliveryDimension = (appliedDimensions) => findDimensionByLabels(
  appliedDimensions,
  [FULFILLMENT_DIMENSIONS_LABELS.FREE_DELIVERY, FULFILLMENT_DIMENSIONS_LABELS.DELIVERY]
);

export const areMajorityOfProductsMajorAppliances = (products) => {
  if (!products?.length) {
    return false;
  }
  const majorApplianceCount = products.filter(
    (product) => product?.identifiers?.productType === 'MAJOR_APPLIANCE'
  )?.length;
  return majorApplianceCount / products.length >= 0.5;
};

// returns only fulfillment filters out of all selected filters
export const getFulfillmentDimension = (appliedDimensions) => {
  return appliedDimensions?.find((dim) => isPlpDimensionLabel(dim?.label, true));
};

// returns following values for selected fulfillment options and
// default to Browse All when no fulfillment filter is applied
// instore -> Available
// Delivery -> Delivery
// Free Delivery -> Free Delivery
// Browse All -> Browse All
export const getAppliedDimensionLabel = (appliedFulfillmentDimension) => {
  const refinementLabel = appliedFulfillmentDimension?.refinements?.[0]?.label;
  return resetDimensionLabel(
    appliedFulfillmentDimension?.label,
    refinementLabel,
    true
  ) ?? BROWSE_ALL_REFINEMENT.label;
};

/**
 * Config for mapping dimensions/refinements to fulfillment option view models.
 * To add a new fulfillment option, add an entry here.
 */
export const FULFILLMENT_VARIANTS = {
  PICKUP: 'pickup',
  DELIVERY: 'delivery'
};

const FULFILLMENT_OPTIONS_CONFIG = [
  {
    dimensionKey: FULFILLMENT_DIMENSIONS_LABELS.GET_IT_FAST,
    refinementMatch: (ref) => ref?.label?.includes(REFINEMENT_LABELS.PICKUP_TODAY),
    uiLabel: FULFILLMENT_UI_LABELS.AVAILABLE_IN_STORE,
    variant: FULFILLMENT_VARIANTS.PICKUP,
    iconOptions: { icon: Store, color: 'brand' }
  },
  {
    dimensionKey: FULFILLMENT_DIMENSIONS_LABELS.DELIVERY,
    refinementMatch: (ref) => ref?.label === REFINEMENT_LABELS.TODAY,
    uiLabel: FULFILLMENT_UI_LABELS.TODAY,
    variant: FULFILLMENT_VARIANTS.DELIVERY,
    iconOptions: { icon: Delivery, color: 'brand' }
  },
  {
    dimensionKey: FULFILLMENT_DIMENSIONS_LABELS.DELIVERY,
    refinementMatch: (ref) => ref?.label === REFINEMENT_LABELS.BY_TOMORROW,
    uiLabel: FULFILLMENT_UI_LABELS.TOMORROW,
    variant: FULFILLMENT_VARIANTS.DELIVERY,
    iconOptions: { icon: Delivery, color: 'brand' }
  },
  {
    dimensionKey: FULFILLMENT_DIMENSIONS_LABELS.FREE_DELIVERY,
    refinementMatch: (ref) => ref?.label === REFINEMENT_LABELS.ANYTIME,
    uiLabel: FULFILLMENT_UI_LABELS.FREE_DELIVERY,
    variant: FULFILLMENT_VARIANTS.DELIVERY,
    iconOptions: { icon: Delivery, color: 'success' }
  },
  // Edge cases: SSR returns delivery refinements under "Get It Fast" instead of the
  // new PLP Speed API's separate Delivery / Free Delivery dimensions.
  // appliedOnly: only produce a pill when the refinement is the active selection,
  // since these labels also appear as non-selected refinements in dimensions.
  {
    dimensionKey: FULFILLMENT_DIMENSIONS_LABELS.GET_IT_FAST,
    refinementMatch: (ref) => ref?.label === REFINEMENT_LABELS.SAME_DAY_DELIVERY,
    uiLabel: FULFILLMENT_UI_LABELS.TODAY,
    variant: FULFILLMENT_VARIANTS.DELIVERY,
    iconOptions: { icon: Delivery, color: 'brand' },
    appliedOnly: true
  },
  {
    dimensionKey: FULFILLMENT_DIMENSIONS_LABELS.GET_IT_FAST,
    refinementMatch: (ref) => ref?.label === REFINEMENT_LABELS.NEXT_DAY_DELIVERY,
    uiLabel: FULFILLMENT_UI_LABELS.TOMORROW,
    variant: FULFILLMENT_VARIANTS.DELIVERY,
    iconOptions: { icon: Delivery, color: 'brand' },
    appliedOnly: true
  },
  {
    dimensionKey: FULFILLMENT_DIMENSIONS_LABELS.GET_IT_FAST,
    refinementMatch: (ref) => ref?.label === REFINEMENT_LABELS.FREE_1_2_DAY_DELIVERY,
    uiLabel: FULFILLMENT_UI_LABELS.FREE_DELIVERY,
    variant: FULFILLMENT_VARIANTS.DELIVERY,
    iconOptions: { icon: Delivery, color: 'success' },
    appliedOnly: true
  }
];

/**
 * Builds render-ready fulfillment option view models from raw dimensions.
 * Only fulfillment-related applied dimensions are matched, since
 * appliedDimensions can include other non-fulfillment categories.
 */
export const getFulfillmentOptionsViewModel = (dimensions = [], appliedDimensions = []) => {
  const findDimension = (key) => dimensions.find(
    (dim) => (dim.label || '').toUpperCase() === key
  );
  const findAppliedDimension = (key) => appliedDimensions.find(
    (dim) => (dim.label || '').toUpperCase() === key
  );

  let initialSelection = null;

  const fulfillmentOptions = FULFILLMENT_OPTIONS_CONFIG.reduce((acc, config) => {
    // Skip if we already have an option with the same UI label
    if (acc.some((opt) => opt.label === config.uiLabel)) {
      return acc;
    }

    const dimension = findDimension(config.dimensionKey);
    const appliedDimension = findAppliedDimension(config.dimensionKey);

    // appliedOnly configs only match when the refinement is the active selection
    const refinement = config.appliedOnly
      ? appliedDimension?.refinements?.find(config.refinementMatch)
      : dimension?.refinements?.find(config.refinementMatch)
        || appliedDimension?.refinements?.find(config.refinementMatch);

    if (!refinement) {
      return acc;
    }

    const isApplied = !initialSelection && appliedDimension?.refinements?.some(config.refinementMatch);

    // Skip pills with no matching products unless the refinement is currently applied
    if (!isApplied && parseInt(refinement.recordCount, 10) === 0) {
      return acc;
    }

    const option = {
      label: config.uiLabel,
      refinementKey: refinement.refinementKey,
      variant: config.variant,
      iconOptions: config.iconOptions,
      sourceDimension: config.dimensionKey,
      sourceRefinement: refinement,
      ...(isApplied && { selected: true })
    };

    if (isApplied) {
      initialSelection = option;
    }

    acc.push(option);
    return acc;
  }, []);

  return { fulfillmentOptions, initialSelection };
};

export const getFulfillmentAnalyticsByLabel = (label) => {
  const analytics = {
    filterType: 'primary',
    type: GET_IT_FAST_DIMENSION_LABEL,
    value: label
  };

  if (label === FULFILLMENT_UI_LABELS.AVAILABLE_IN_STORE) {
    analytics.nearbyStore = 'y';
  }

  if (label === FULFILLMENT_UI_LABELS.TODAY) {
    analytics.speed = REFINEMENT_LABELS.TODAY;
    analytics.freeDelivery = 'n';
  }

  if (label === FULFILLMENT_UI_LABELS.TOMORROW) {
    analytics.speed = REFINEMENT_LABELS.BY_TOMORROW;
    analytics.freeDelivery = 'n';
  }

  if (label === FULFILLMENT_UI_LABELS.FREE_DELIVERY) {
    analytics.value = FULFILLMENT_UI_LABELS.DELIVERY;
    analytics.speed = REFINEMENT_LABELS.ANYTIME;
    analytics.freeDelivery = 'y';
  }

  return analytics;
};